import { httpClient } from '@shared/infrastructure/http/http-client.js';
import type {
  GazeDirection,
  GestureLabel,
  GestureLabelDraft,
  GestureLabelScope,
  VideoGestureLabels,
} from '../model.js';

interface GestureLabelPayload {
  label_id: string;
  gesture_name: string;
  start_time_ms: number;
  end_time_ms: number;
  gaze_direction?: string | null;
  labeled_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/** Virtual Profiler's document without its `_id`, passed through by the back-office as it is. */
interface VideoGestureLabelsPayload {
  scope?: string | null;
  source_type?: string | null;
  source_id?: number | string | null;
  labels?: GestureLabelPayload[] | null;
  complete?: boolean | null;
  completed_by?: string | null;
  completed_at?: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
  version?: number | null;
}

const GAZE_DIRECTIONS: ReadonlySet<string> = new Set<GazeDirection>([
  'up_left',
  'up',
  'up_right',
  'left',
  'right',
  'down_left',
  'down',
  'down_right',
]);

function toDate(value: string | null | undefined): Date | null {
  return value ? new Date(value) : null;
}

/**
 * A direction outside the engine's buckets reads as none: coherence binds a gaze rule by bucket, so
 * such a label could never reach a rule, and showing it as a direction would say otherwise.
 */
function toGaze(value: string | null | undefined): GazeDirection | null {
  return typeof value === 'string' && GAZE_DIRECTIONS.has(value) ? (value as GazeDirection) : null;
}

function toLabel(payload: GestureLabelPayload): GestureLabel {
  return {
    labelId: payload.label_id,
    gestureName: payload.gesture_name,
    startMs: payload.start_time_ms,
    endMs: payload.end_time_ms,
    gazeDirection: toGaze(payload.gaze_direction),
    labeledBy: payload.labeled_by ?? null,
    createdAt: toDate(payload.created_at),
    updatedAt: toDate(payload.updated_at),
  };
}

/**
 * The scope is the one asked for: each call reads or writes one scope's document, and an answer
 * without the field (a Virtual Profiler older than the scopes) is that document all the same.
 */
function toDocument(
  payload: VideoGestureLabelsPayload,
  videoId: number,
  scope: GestureLabelScope,
): VideoGestureLabels {
  return {
    scope,
    sourceType: 'manual_analysis',
    // Upstream passes ids through, and a source id has travelled as text before.
    sourceId:
      payload.source_id === null || payload.source_id === undefined
        ? videoId
        : Number(payload.source_id),
    labels: (payload.labels ?? []).map(toLabel),
    complete: payload.complete === true,
    completedBy: payload.completed_by ?? null,
    completedAt: toDate(payload.completed_at),
    updatedBy: payload.updated_by ?? null,
    updatedAt: toDate(payload.updated_at),
    version: typeof payload.version === 'number' ? payload.version : 0,
  };
}

/**
 * A write's answer: the whole document after it, or null when it came back without one.
 *
 * Every write is answered with the document, so the screen can show it without asking again. A
 * gateway that answers with no content (a 204) must not read as a video with no labels: null tells
 * the caller to read it again instead.
 */
function toWritten(
  payload: VideoGestureLabelsPayload | null | undefined,
  videoId: number,
  scope: GestureLabelScope,
): VideoGestureLabels | null {
  return payload ? toDocument(payload, videoId, scope) : null;
}

/** The body of a label: the API's spelling, the gaze direction only when there is one. */
function toBody(draft: GestureLabelDraft): Record<string, unknown> {
  return {
    gestureName: draft.gestureName,
    startMs: draft.startMs,
    endMs: draft.endMs,
    ...(draft.gazeDirection ? { gazeDirection: draft.gazeDirection } : {}),
  };
}

function base(videoId: number): string {
  return `/virtual-profiler/videos/${videoId}/gesture-labels`;
}

/**
 * The scope always travels, `gestures` included: the gateway defaults to it, but a call that says
 * which document it means is one that cannot land on the other by a default changing.
 */
function withScope(url: string, scope: GestureLabelScope): string {
  return `${url}?scope=${scope}`;
}

/**
 * Boundary with the gestures a person labelled on a video (product owner, 2026-09-28).
 *
 * Per video, never per run: the labels are the answer key the engine is measured against and what a
 * semantics control run reads instead of the engine, so they survive every re-analysis. The removal
 * here takes out one label, which is somebody correcting their own annotation; the history of
 * analyses is untouched by anything in this gateway.
 *
 * Every call names the scope (2026-10-29): `gestures`, the video's gestures, or `gesture_alerts`,
 * only its alerts — two documents of the same video, each with its own «complete».
 */
export const gestureLabelsApi = {
  get: async (
    videoId: number,
    scope: GestureLabelScope = 'gestures',
  ): Promise<VideoGestureLabels> =>
    toDocument(
      (await httpClient.get<VideoGestureLabelsPayload>(withScope(base(videoId), scope))) ?? {},
      videoId,
      scope,
    ),

  create: async (
    videoId: number,
    draft: GestureLabelDraft,
    scope: GestureLabelScope = 'gestures',
  ): Promise<VideoGestureLabels | null> =>
    toWritten(
      await httpClient.post<VideoGestureLabelsPayload>(
        withScope(base(videoId), scope),
        toBody(draft),
      ),
      videoId,
      scope,
    ),

  update: async (
    videoId: number,
    labelId: string,
    draft: GestureLabelDraft,
    scope: GestureLabelScope = 'gestures',
  ): Promise<VideoGestureLabels | null> =>
    toWritten(
      await httpClient.put<VideoGestureLabelsPayload>(
        withScope(`${base(videoId)}/${encodeURIComponent(labelId)}`, scope),
        toBody(draft),
      ),
      videoId,
      scope,
    ),

  remove: async (
    videoId: number,
    labelId: string,
    scope: GestureLabelScope = 'gestures',
  ): Promise<VideoGestureLabels | null> =>
    toWritten(
      await httpClient.delete<VideoGestureLabelsPayload>(
        withScope(`${base(videoId)}/${encodeURIComponent(labelId)}`, scope),
      ),
      videoId,
      scope,
    ),

  /**
   * «Etiquetado completo»: every gesture — or every alert — of the video is labelled. A label write
   * never resets it.
   */
  setComplete: async (
    videoId: number,
    complete: boolean,
    scope: GestureLabelScope = 'gestures',
  ): Promise<VideoGestureLabels | null> =>
    toWritten(
      await httpClient.put<VideoGestureLabelsPayload>(
        withScope(`${base(videoId)}/complete`, scope),
        {
          complete,
        },
      ),
      videoId,
      scope,
    ),
};
